import React, { useState } from "react";
import {
  Stack,
  Button,
  TextInput,
  PasswordInput,
  Text,
  Anchor,
  Alert,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconInfoCircle, IconCheck } from "@tabler/icons-react";

import { useApp } from "../../../../contexts/AppContext";

interface SignupFormData {
  companyName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  companyName?: string;
  username?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  general?: string;
}

export interface SignupFormProps {
  /** Callback function called when user successfully registers and should be redirected */
  onRegistrationSuccess?: (userData: any) => void;

  /** Callback function called when user wants to navigate to signin */
  onNavigateToSignin?: () => void;

  /** Whether to show the "Already have an account?" link */
  showSigninLink?: boolean;
}

export const SignupForm: React.FC<SignupFormProps> = ({
  onRegistrationSuccess,
  onNavigateToSignin,
  showSigninLink = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<FormErrors>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const { signUpWithCredentials } = useApp();

  const form = useForm<SignupFormData>({
    initialValues: {
      companyName: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    validate: {
      companyName: (value) => {
        if (!value) return "Company name is required";
        if (value.length < 2)
          return "Company name must be at least 2 characters";
        return null;
      },
      username: (value) => {
        if (!value) return "Username is required";
        if (value.length < 3) return "Username must be at least 3 characters";
        if (!/^[a-zA-Z0-9_-]+$/.test(value))
          return "Username can only contain letters, numbers, hyphens, and underscores";
        return null;
      },
      email: (value) => {
        if (!value) return "Email is required";
        if (!/^\S+@\S+\.\S+$/.test(value)) return "Invalid email format";
        return null;
      },
      password: (value) => {
        if (!value) return "Password is required";
        if (value.length < 8) return "Password must be at least 8 characters";
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          return "Password must contain at least one uppercase letter, one lowercase letter, and one number";
        }
        return null;
      },
      confirmPassword: (value, values) => {
        if (!value) return "Please confirm your password";
        if (value !== values.password) return "Passwords do not match";
        return null;
      },
    },
  });

  const handleSubmit = async (values: SignupFormData) => {
    setLoading(true);
    setServerErrors({});
    setSuccessMessage("");

    try {
      // Call the real AuthService API
      const result = await signUpWithCredentials({
        companyName: values.companyName,
        username: values.username,
        email: values.email,
        password: values.password,
        repeatPassword: values.confirmPassword,
      });

      if (result.didSucceed()) {
        setSuccessMessage("Account created successfully! Redirecting...");

        // Notify parent component that registration was successful
        setTimeout(() => {
          onRegistrationSuccess?.(result.getDataFirst());
        }, 1000);
      } else {
        throw Error(result.getErrorSummary());
      }
    } catch (error) {
      console.error("Signup error:", error);
      setServerErrors({
        general:
          error instanceof Error
            ? error.message
            : "An error occurred while creating your account. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSigninLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onNavigateToSignin?.();
  };

  // Show success state
  if (successMessage) {
    return (
      <Alert color="green" icon={<IconCheck size={16} />}>
        {successMessage}
      </Alert>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {serverErrors.general && (
          <Alert color="red" icon={<IconInfoCircle size={16} />}>
            {serverErrors.general}
          </Alert>
        )}

        <TextInput
          label="Company Name"
          placeholder="Enter your company name"
          description="This will be used to set up your organization"
          required
          {...form.getInputProps("companyName")}
          error={form.errors.companyName || serverErrors.companyName}
        />

        <TextInput
          label="Username"
          placeholder="Enter username"
          description="This will be used to sign in to your account"
          required
          {...form.getInputProps("username")}
          error={form.errors.username || serverErrors.username}
        />

        <TextInput
          label="Email"
          placeholder="Enter your email"
          description="We'll use this to send you important updates or password reset emails"
          type="email"
          required
          {...form.getInputProps("email")}
          error={form.errors.email || serverErrors.email}
        />

        <PasswordInput
          label="Password"
          placeholder="Create a password"
          description="Must be at least 8 characters with uppercase, lowercase, and number"
          required
          {...form.getInputProps("password")}
          error={form.errors.password || serverErrors.password}
        />

        <PasswordInput
          label="Confirm Password"
          placeholder="Confirm your password"
          required
          {...form.getInputProps("confirmPassword")}
          error={form.errors.confirmPassword || serverErrors.confirmPassword}
        />

        <Button type="submit" fullWidth size="md" mt="md" loading={loading}>
          Create Account
        </Button>

        {showSigninLink && (
          <Text ta="center" size="sm" c="dimmed">
            Already have an account?{" "}
            <Anchor
              onClick={handleSigninLinkClick}
              fw={500}
              style={{ cursor: "pointer" }}
            >
              Sign in here
            </Anchor>
          </Text>
        )}
      </Stack>
    </form>
  );
};
